import { HOCKEY_TRADING_COMPETITION_CODE, FOOTBALL_TRADING_COMPETITION_CODE, canonicalCompetitionCode } from './competitionIdentity';

export type MarketConferenceFilter = 'ALL' | 'EAST' | 'WEST' | 'AFC' | 'NFC';
export type MarketDivisionFilter = 'ALL' | 'ATLANTIC' | 'METROPOLITAN' | 'CENTRAL' | 'PACIFIC'
  | 'AFC_EAST' | 'AFC_NORTH' | 'AFC_SOUTH' | 'AFC_WEST'
  | 'NFC_EAST' | 'NFC_NORTH' | 'NFC_SOUTH' | 'NFC_WEST';

type StructureTeam = {
  conference_code: string | null;
  division_code: string | null;
};

const DIVISIONS: Readonly<Record<Exclude<MarketConferenceFilter, 'ALL'>, readonly Exclude<MarketDivisionFilter, 'ALL'>[]>> = {
  EAST: ['ATLANTIC', 'METROPOLITAN'],
  WEST: ['CENTRAL', 'PACIFIC'],
  AFC: ['AFC_EAST', 'AFC_NORTH', 'AFC_SOUTH', 'AFC_WEST'],
  NFC: ['NFC_EAST', 'NFC_NORTH', 'NFC_SOUTH', 'NFC_WEST'],
};

function normalized(value: string | null): string {
  return (value ?? '').trim().toUpperCase();
}

export function isStructureFilterMarket(competitionCode: string | null | undefined, phase: string | null | undefined): boolean {
  const code = canonicalCompetitionCode(competitionCode);
  return (code === HOCKEY_TRADING_COMPETITION_CODE || code === FOOTBALL_TRADING_COMPETITION_CODE) && phase === 'REGULAR_SEASON';
}

export function marketStructureOptions(teams: readonly StructureTeam[], conference: MarketConferenceFilter): {
  conferences: Exclude<MarketConferenceFilter, 'ALL'>[];
  divisions: Exclude<MarketDivisionFilter, 'ALL'>[];
} {
  const conferences = (['EAST', 'WEST', 'AFC', 'NFC'] as const).filter((code) => teams.some((team) => normalized(team.conference_code) === code));
  const divisions = (conference === 'ALL' ? Object.values(DIVISIONS).flat() : DIVISIONS[conference])
    .filter((code) => teams.some((team) => (
      normalized(team.division_code) === code
      && DIVISIONS[normalized(team.conference_code) as Exclude<MarketConferenceFilter, 'ALL'>]?.includes(code)
    )));
  return { conferences, divisions };
}

export function nextMarketConferenceSelection(conference: MarketConferenceFilter, division: MarketDivisionFilter): {
  conference: MarketConferenceFilter;
  division: MarketDivisionFilter;
} {
  return {
    conference,
    division: conference === 'ALL' || division === 'ALL' || DIVISIONS[conference].includes(division as Exclude<MarketDivisionFilter, 'ALL'>)
      ? division : 'ALL',
  };
}

export function matchesMarketStructure(team: StructureTeam, conference: MarketConferenceFilter, division: MarketDivisionFilter): boolean {
  return (conference === 'ALL' || normalized(team.conference_code) === conference)
    && (division === 'ALL' || normalized(team.division_code) === division);
}

export function filterMarketSections<T extends StructureTeam, S extends {
  topLevelCode: string;
  kind: string;
  data: T[];
  showCutLineAfter: boolean;
}>(sections: readonly S[], conference: MarketConferenceFilter, division: MarketDivisionFilter): S[] {
  const visible = sections
    .map((section) => ({ ...section, data: section.data.filter((team) => matchesMarketStructure(team, conference, division)) }))
    .filter((section) => section.data.length > 0);
  return visible.map((section) => ({
    ...section,
    showCutLineAfter: section.showCutLineAfter && visible.some((other) => other.topLevelCode === section.topLevelCode && other.kind === 'OUTSIDE'),
  })) as S[];
}
